import { MarketoServiceError } from './errors'
import { type MarketoFormContext, type MarketoServiceConfig } from './types'
import { injectMarketoForms } from './utils'

const MARKETO_FORM_CONTAINER_PREFIX = 'mktoForm_'
const MARKETO_SUBMIT_TIMEOUT_MS = 15_000

export class MarketoService {
  constructor(private readonly config: MarketoServiceConfig) {}

  async submitForm(
    context: MarketoFormContext,
    options: { formId?: string | number } = {}
  ): Promise<void> {
    const { baseUrl, munchkinId } = this.config
    const formId = options.formId ?? this.config.formId

    if (!baseUrl || !munchkinId || !formId) {
      throw new MarketoServiceError('Marketo is not configured')
    }

    try {
      await injectMarketoForms(baseUrl)
      this.resetFormContainer(formId)
      await this.loadAndSubmit(
        baseUrl,
        munchkinId,
        Number(formId),
        this.toMktoValues(context)
      )
    } catch (error) {
      if (error instanceof MarketoServiceError) {
        throw error
      }
      throw new MarketoServiceError(
        'Failed to submit Marketo form',
        error as Error
      )
    }
  }

  private toMktoValues(
    formContext: MarketoFormContext
  ): Record<string, string> {
    const { firstName, lastName, email, domain, context } = formContext

    return {
      ...(firstName && { FirstName: firstName }),
      ...(lastName && { LastName: lastName }),
      ...(email && { Email: email }),
      ...(domain && { Domain__c: domain }),
      ...(context && { inAppExpansionRequestType: context }),
    }
  }

  private resetFormContainer(formId: string | number): void {
    const containerId = `${MARKETO_FORM_CONTAINER_PREFIX}${formId}`

    document.getElementById(containerId)?.remove()

    const form = document.createElement('form')
    form.id = containerId
    form.style.display = 'none'
    document.body.appendChild(form)
  }

  private loadAndSubmit(
    baseUrl: string,
    munchkinId: string,
    formId: number,
    values: Record<string, string>
  ): Promise<void> {
    const mktoForms2 = window.MktoForms2

    if (!mktoForms2) {
      return Promise.reject(
        new MarketoServiceError('Marketo forms library is not available')
      )
    }

    return new Promise((resolve, reject) => {
      // forms2 exposes no reliable submit-error hook, so bound the wait: reject if
      // onSuccess never fires (validation block, failed POST, callback never runs).
      const timeoutId = setTimeout(() => {
        reject(new MarketoServiceError('Marketo form submission timed out'))
      }, MARKETO_SUBMIT_TIMEOUT_MS)

      mktoForms2.loadForm(baseUrl, munchkinId, formId, (form) => {
        // Marketo runs this callback with form === null when the id isn't in the
        // instance; fail fast with a clear error instead of hanging until timeout.
        if (!form) {
          clearTimeout(timeoutId)
          reject(
            new MarketoServiceError(
              `Marketo form ${formId} was not found in this instance`
            )
          )
          return
        }

        // Return false from onSuccess to suppress Marketo's default thank-you redirect.
        form.onSuccess(() => {
          clearTimeout(timeoutId)
          resolve()
          return false
        })
        form.setValues(values)
        form.submit()
      })
    })
  }
}
